import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function TermsOfUse() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-white font-body">
      <nav className="sticky top-0 z-50 bg-[#090D17] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="h-16 flex items-center justify-between">
            <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/')}>
              <img src="https://customer-assets-jai6qajn.emergentagent.net/job_38d96a59-41a3-4967-8296-b7ba8cd6e1a1/artifacts/rrpmz7yf_logo%20%287%29.png" alt="OwnerSide" className="h-7 w-7 object-contain" />
              <span className="font-heading text-lg font-semibold tracking-tight text-white">OwnerSide</span>
            </div>
            <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors">
              <ArrowLeft className="h-3.5 w-3.5" /> Home
            </button>
          </div>
        </div>
      </nav>
      <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-20">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-950 mb-3">Terms of Use</h1>
        <p className="text-sm text-slate-400 mb-12">Last updated: August 18, 2026</p>

        <div className="prose-custom space-y-10 text-slate-600 text-[15px] leading-relaxed">
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">1. Acceptance of Terms</h2>
            <p>By accessing and using the OwnerSide website ("Service"), operated by OwnerSide Business Services Ltd. ("Company," "we," "our," or "us"), you agree to be bound by these Terms of Use ("Terms"). If you do not agree to these Terms, you must not use the Service.</p>
            <p className="mt-3">These Terms constitute a legally binding agreement between you and OwnerSide Business Services Ltd. Please read them carefully before using the Service.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">2. Description of Service</h2>
            <p>OwnerSide is a business advisory and executive support company that helps owner-managed businesses understand, control and improve their operations. Depending on the engagement, our services may include:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li>Business and financial analysis.</li>
              <li>Management reporting, KPI development and executive dashboards.</li>
              <li>Financial modelling and forecasting.</li>
              <li>Contract and obligation analysis.</li>
              <li>Due diligence and financing preparation.</li>
              <li>Transaction and special-project support.</li>
              <li>Project-based or ongoing executive support, as agreed with each client.</li>
            </ul>
            <p className="mt-3">The specific scope of services is agreed individually with each client and may vary from what is described on our website.</p>
            <p className="mt-3">Specific client engagements are governed by separate written agreements. If there is any conflict between these Terms and a client engagement agreement, the engagement agreement will prevail.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">3. Important Disclaimer</h2>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm">
              <p className="font-semibold text-amber-900 mb-2">OwnerSide is NOT a law firm, accounting firm, or investment adviser.</p>
              <p className="text-amber-800">The business advisory, reporting, and executive support services we provide are for general business management purposes and do not constitute legal, tax, accounting, or investment advice. Where legal, tax, accounting, or investment matters are involved, clients should obtain independent professional advice from a qualified lawyer, accountant, or investment adviser as appropriate.</p>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">4. User Obligations</h2>
            <p>By using the Service, you agree to:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li>Provide accurate, truthful, and complete information in all submissions.</li>
              <li>Use the Service only for lawful business purposes.</li>
              <li>Not submit false, misleading, or fraudulent information.</li>
              <li>Not attempt to gain unauthorized access to the Service or its systems.</li>
              <li>Not use automated tools (bots, scrapers) to access the Service without our prior written consent.</li>
              <li>Comply with all applicable laws and regulations.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">5. Nature of Advisory Services</h2>
            <p>Our advisory and reporting services are based on information provided by the client and our professional judgment at the time services are rendered. You acknowledge and agree that:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li>The quality and accuracy of our analysis and reporting depends in part on the completeness and accuracy of information you provide.</li>
              <li>Our services do not guarantee any particular business, financial, or transactional outcome.</li>
              <li>Recommendations are advisory in nature, and business decisions remain the responsibility of the client.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">6. Intellectual Property</h2>
            <p>All content, features, and functionality of the Service — including but not limited to text, graphics, logos, icons, software, and design elements — are the exclusive property of OwnerSide Business Services Ltd. and are protected by intellectual property laws.</p>
            <p className="mt-3">You are granted a limited, non-exclusive, non-transferable license to access and use the Service for legitimate business purposes in connection with engaging our services. You may not copy, modify, distribute, sell, or create derivative works based on the Service without our express written permission.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">7. Privacy</h2>
            <p>Your use of the Service is also governed by our <Link to="/privacy" className="text-[#0E7C72] hover:underline">Privacy Policy</Link>, which describes how we collect, use, and protect your personal information. Our collection, use and disclosure of personal information is described in our Privacy Policy.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">8. Limitation of Liability</h2>
            <p>To the maximum extent permitted by applicable law:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li>The Service is provided on an "as is" and "as available" basis without warranties of any kind, whether express or implied.</li>
              <li>We do not warrant that the Service will be uninterrupted, error-free, or secure.</li>
              <li>We shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service.</li>
              <li>Our total liability for any claim arising from or related to the Service shall not exceed the amount you paid to us (if any) in the 12 months preceding the claim.</li>
              <li>We are not liable for outcomes or losses resulting from a client's failure to obtain independent legal, tax, accounting, or investment advice where appropriate.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">9. Indemnification</h2>
            <p>You agree to indemnify, defend, and hold harmless OwnerSide Business Services Ltd. and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, or expenses (including reasonable legal fees) arising from your use of the Service, violation of these Terms, or infringement of any third-party rights.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">10. Termination</h2>
            <p>We reserve the right to suspend or terminate your access to the Service at any time, without notice, for any reason, including violation of these Terms. Upon termination, your right to use the Service will immediately cease. Provisions that by their nature should survive termination (including intellectual property, limitation of liability, and indemnification) shall survive.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">11. Governing Law</h2>
            <p>These Terms are governed by and construed in accordance with the laws of the Province of Alberta and the applicable federal laws of Canada. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of Alberta, Canada.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">12. Changes to These Terms</h2>
            <p>We may modify these Terms at any time. Material changes will be posted on our website with a revised "Last updated" date. Your continued use of the Service after changes are posted constitutes your acceptance of the revised Terms.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">13. Contact Us</h2>
            <p>If you have questions about these Terms, please contact us:</p>
            <div className="mt-3 bg-slate-50 rounded-xl p-5 text-sm space-y-1">
              <p><strong>OwnerSide Business Services Ltd.</strong></p>
              <p>Email: info@owner-side.com</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
